// Containers
import SegmentContainer from "@/components/share-of-voice/filters/SegmentContainer.tsx";
import SOVFilterContainer from "@/components/share-of-voice/filters/ShareOfVoiceFilterContainer";
import { Button } from "@/components/ui/button";
import { IconButton } from "@/components/ui/icon-button";
import {
  ResizableHandle,
  ResizablePanelGroup,
} from "@/components/ui/resizable.tsx";
import { ResizeHandle } from "@/components/ui/resize-handle";
import type { ShareVoiceSidebarCategory } from "@/data-access/share-of-voice/queries/shareOfVoice-sidebar-filter-queries";
import { useResizeHandle } from "@/hooks/useResizeHandle";
import { cn } from "@/lib/utils";
import { SidebarSimpleIcon } from "@phosphor-icons/react";
import { useEffect, useLayoutEffect, useRef, useState } from "react";

const MIN_FILTER_WIDTH = 240;
const COLLAPSED_WIDTH = 55;
const LARGE_SCREEN_BREAKPOINT = 1024; // md breakpoint

interface ShareOfVoiceFilterSideBarProps {
  selectedFilters: Map<ShareVoiceSidebarCategory, string[]>;
  toggleFilterSelection: (
    category: ShareVoiceSidebarCategory,
    item: string | number,
  ) => void;
}

const ShareOfVoiceFilterSideBar = ({
  selectedFilters,
  toggleFilterSelection,
}: ShareOfVoiceFilterSideBarProps) => {
  const [width, setWidth] = useState(COLLAPSED_WIDTH);
  const [isExpanded, setIsExpanded] = useState(false);
  const [maxWidth, setMaxWidth] = useState(window.innerWidth * 0.33);
  const hasInitialRender = useRef(false);
  const widthRef = useRef(width);
  const isExpandedRef = useRef(isExpanded);

  // Keep refs in sync with state
  useEffect(() => {
    widthRef.current = width;
  }, [width]);

  useEffect(() => {
    isExpandedRef.current = isExpanded;
  }, [isExpanded]);

  useEffect(() => {
    // Check if screen is large enough on mount
    const isLargeScreen = window.innerWidth >= LARGE_SCREEN_BREAKPOINT;
    if (isLargeScreen) {
      // Default expanded width
      setWidth(300);
      setIsExpanded(true);
    }
  }, []);

  // Handle window resize to collapse sidebar on smaller screens and update max width
  // Empty dependency array is intentional: we use refs (isExpandedRef, widthRef) to access
  // current state values, which are kept in sync via separate useEffect hooks above.
  // This ensures the resize listener is only set up once on mount and cleaned up on unmount,
  // while always reading the latest state values through refs.
  useEffect(() => {
    const handleResize = () => {
      const isLargeScreen = window.innerWidth >= LARGE_SCREEN_BREAKPOINT;
      const currentIsExpanded = isExpandedRef.current;
      const currentWidth = widthRef.current;

      if (!isLargeScreen && currentIsExpanded) {
        setWidth(COLLAPSED_WIDTH);
        setIsExpanded(false);
      }
      // Update max width to 33% of viewport width
      const newMaxWidth = window.innerWidth * 0.33;
      setMaxWidth(newMaxWidth);
      // Constrain current width to new max width if needed
      if (currentIsExpanded && currentWidth > newMaxWidth) {
        // Clamp width between MIN_FILTER_WIDTH and newMaxWidth
        const constrainedWidth = Math.max(
          MIN_FILTER_WIDTH,
          Math.min(newMaxWidth, currentWidth),
        );
        setWidth(constrainedWidth);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const { handleMouseDown, isResizing } = useResizeHandle({
    minWidth: MIN_FILTER_WIDTH,
    maxWidth,
    getCurrentWidth: () => width,
    onWidthChange: setWidth,
  });

  // Skip initial animation after first render
  useLayoutEffect(() => {
    hasInitialRender.current = true;
  }, []);

  const handleToggle = () => {
    if (isExpanded) {
      setWidth(COLLAPSED_WIDTH);
    } else {
      setWidth(300); // Default expanded width
    }
    setIsExpanded(!isExpanded);
  };

  // If collapsed, render as floating button
  if (!isExpanded) {
    return (
      <div className="pt-4 px-4">
        <Button
          onPress={handleToggle}
          variant={"outline"}
          className={
            "flex items-center gap-1 rounded-3xl shadow-[0_0_20px_rgba(147,51,234,0.1)]"
          }
          size={"sm"}
        >
          <SidebarSimpleIcon className={"w-4 h-4 text-gray-500"} />
          Show filters
        </Button>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "relative w-full",
        isResizing && "select-none",
        "transition-all duration-200 ease-in-out",
      )}
      style={{ width }}
    >
      <div
        className={cn(
          "overflow-auto h-[calc(100vh-80px)] relative border-r border-slate-100",
          "scrollbar-thin scrollbar-thumb-slate-200 scrollbar-track-transparent hover:scrollbar-thumb-slate-300",
        )}
      >
        <div className="sticky top-0 bg-white flex items-center justify-between px-1.5 py-1 z-20">
          <IconButton
            icon={<SidebarSimpleIcon size={16} />}
            variant="ghost"
            size="md"
            onClick={handleToggle}
            aria-label="Collapse filters"
            className="ml-auto text-gray-600"
          />
        </div>
        <div className="h-[calc(100%-52px)]">
          <ResizablePanelGroup direction="vertical" className="h-full">
            <SegmentContainer />
            <ResizableHandle />
            <SOVFilterContainer
              selectedFilters={selectedFilters}
              toggleFilterSelection={toggleFilterSelection}
            />
          </ResizablePanelGroup>
        </div>
      </div>
      <div className="hidden md:block">
        <ResizeHandle onMouseDown={handleMouseDown} className="z-20" />
      </div>
    </div>
  );
};

export default ShareOfVoiceFilterSideBar;
